import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "./firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { Search, X } from 'react-bootstrap-icons';
import Header from './components/Header';
import Footer from './components/Footer';
import './controlroom.css';

const ControlPage = () => {
  const [user, loading, error] = useAuthState(auth);
  const [clubStats, setClubStats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [riders, setRiders] = useState([]);
  const [pnrCounts, setPnrCounts] = useState({
    total: 0,
    checkedIn: 0,
    goodieBagCollected: 0,
    byRoomType: {}
  });
  
  const navigate = useNavigate();
  const [selectedClub, setSelectedClub] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const getClubMembers = (clubName) => {
    return riders.filter(rider => rider.club === clubName);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedClub(null);
  };

  const handleClubClick = (club) => {
    setSelectedClub(club);
    setShowModal(true);
  };

  const PNR_NAMES = {
    'CC': 'Clan Cluster',
    'DS': 'Desert Squad',
    'CO': 'Cool Oasis',
    'SL': 'Starlight Camp',
    'TS': 'Twin Shack'
  };

  const getPNRType = (pnr) => {
    if (!pnr) return null;
    if (pnr.includes('CC')) return 'CC';
    if (pnr.includes('DS')) return 'DS';
    if (pnr.includes('TS')) return 'TS';
    if (pnr.includes('CO')) return 'CO';
    if (pnr.includes('SL')) return 'SL';
    return null;
  };

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, "RiderMania", "2026", "Registrations"), where("status", "==", "success")),
      (snapshot) => {
        const ridersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRiders(ridersData);

        // Calculate club-wise statistics
        const clubsMap = new Map();
        ridersData.forEach(rider => {
          if (rider.club) {
            if (!clubsMap.has(rider.club)) {
              clubsMap.set(rider.club, {
                name: rider.club,
                total: 0,
                checkedIn: 0,
                goodieBagCollected: 0
              });
            }
            
            const clubStats = clubsMap.get(rider.club);
            clubStats.total++;
            if (rider.checkIntime) {
              clubStats.checkedIn++;
            }
            if (rider.goodieBagStatus) {
              clubStats.goodieBagCollected++;
            }
          }
        });

        // Convert Map to array and sort by club name
        const clubStatsArray = Array.from(clubsMap.values())
          .sort((a, b) => a.name.localeCompare(b.name));
      
        setClubStats(clubStatsArray);
        
        // Calculate counts per room type (udf2)
        const counts = {
          total: ridersData.length,
          checkedIn: ridersData.filter(rider => rider.checkIntime).length,
          goodieBagCollected: ridersData.filter(rider => rider.goodieBagStatus).length,
          byRoomType: {}
        };

        ridersData.forEach(rider => {
          const roomType = rider.udf2 || 'N/A';
          if (!counts.byRoomType[roomType]) {
            counts.byRoomType[roomType] = { total: 0, checkedIn: 0, goodieBagCollected: 0 };
          }
          counts.byRoomType[roomType].total++;
          if (rider.checkIntime) {
            counts.byRoomType[roomType].checkedIn++;
          }
          if (rider.goodieBagStatus) {
            counts.byRoomType[roomType].goodieBagCollected++;
          }
        });

        setPnrCounts(counts);
      },
      (error) => {
        console.error("Error fetching riders:", error);
      }
    );

    return () => unsubscribe();
  }, []);

  const filteredClubs = clubStats.filter(club =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="control-room-page">
      <Header title="Live Check-in Statistics" showBack={false} showDashboard={true} />
      
      <div className="control-room-container">
              
        <div className="search-container-custom">
          <div className="search-box-custom">
            <Search className="search-icon-custom" size={18} />
            <input
              type="text"
              placeholder="Search clubs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input-custom"
            />
            {searchTerm && (
              <button className="clear-search-custom" onClick={() => setSearchTerm('')}>
                <X size={20} />
              </button>
            )}
          </div>
        </div>

        {/* PNR Counts Card */}
        <div className="control-card">
          <div className="card-header-custom mb-4 border-bottom pb-3">
            <h4 className="m-0">Overview</h4>
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">Total Riders</span>
              <div className="stat-value">{pnrCounts.total}</div>
              <div className="stat-subtext">{pnrCounts.checkedIn} Checked In</div>
             
            </div>
            
            {Object.entries(pnrCounts.byRoomType || {})
              .map(([roomType, count]) => (
                <div key={roomType} className="stat-item">
                  <span className="stat-label">{roomType}</span>
                  <div className="stat-value">{count.total}</div>
                  <div className="stat-subtext">{count.checkedIn} Checked In</div>
                 
                </div>
              ))}
          </div>
        </div>

        {/* Club Statistics */}
        <div className="control-card">
          <div className="card-header-custom mb-4 border-bottom pb-3">
            <h4 className="m-0">Club Statistics</h4>
          </div>
          <div className="custom-table-container">
            <table className="custom-table">
              <thead>
                <tr>
                  <th>Club Name</th>
                  <th className="text-center">Total</th>
                  <th className="text-center">Checked In</th>                 
                  <th>Progress</th>
                </tr>
              </thead>
              <tbody>
                {filteredClubs.map(club => (
                  <tr key={club.name} onClick={() => handleClubClick(club)}>
                    <td><strong>{club.name}</strong></td>
                    <td className="text-center">{club.total}</td>
                    <td className="text-center">{club.checkedIn}</td>                  
                    <td>
                      <div className="progress-container">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${(club.checkedIn / club.total) * 100}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">{Math.round((club.checkedIn / club.total) * 100)}%</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Footer />

      {/* Club Members Modal */}
      {showModal && selectedClub && (
        <div className="modal-overlay-custom" onClick={handleCloseModal} style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)' }}>
          <div className="modal-content-custom" onClick={e => e.stopPropagation()} style={{ backgroundColor: '#212529', color: '#f8f9fa' }}>
            <div className="modal-header-custom">
              <h3>
                {selectedClub.name}
                <span className="badge-custom badge-info">
                  {selectedClub.checkedIn}/{selectedClub.total} Checked In
                </span>
              </h3>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                &times;
              </button>
            </div>
            <div className="modal-body-custom">
              <div className="custom-table-container">
                <table className="custom-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>BOBMC ID</th>
                      <th className="text-center">Status</th>
                      <th className="text-right">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getClubMembers(selectedClub.name)
                      .sort((a, b) => {
                        if (a.checkIntime && !b.checkIntime) return -1;
                        if (!a.checkIntime && b.checkIntime) return 1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(member => (
                        <tr key={member.id}>
                          <td>
                            <div style={{fontWeight: '500'}}>{member.name}</div>
                          </td>
                          <td className="rider-profile-id">{member.riderprofile}</td>
                          <td className="text-center">
                            {member.checkIntime ? (
                              <span className="badge-custom badge-success">Checked In</span>
                            ) : (
                              <span className="badge-custom badge-warning">Pending</span>
                            )}
                          </td>
                          <td className="text-right checkin-time">
                            {member.checkIntime 
                              ? member.checkIntime.toDate().toLocaleString()
                              : '-'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ControlPage;
